/**
 * The category surface (P7-T1).
 *
 * A category is a label with a reversible visibility, and this panel is exactly that: create,
 * rename, archive/unarchive, and the reference-safe deletion. Nothing here shows a figure it
 * computed — `formatCategoryTotals` prints the per-currency totals the read derived, and an
 * archived category keeps them because archiving is visibility rather than a money change.
 *
 * The deletion is the interesting control, and it is deliberately the least forgiving: a category
 * that an entry still carries cannot be removed, so the API refuses `409` and the panel prints the
 * refusal the envelope named (``/references`, `code: 'entry'``) together with the reversible
 * alternative. The refusal is the protection, so it is shown rather than swallowed.
 */

import { useState } from 'react';

import type { CategoryBody } from '@saldo/contracts';

import {
  categoryViews,
  categoriesFor,
  formatCategoryTotals,
  CATEGORY_NAME_LIMIT,
  type CategoryTarget,
  type CategoryView,
} from './categories';
import { describeSaveState, type SaveState } from './save';

export interface CategoryPanelProps {
  readonly categories: readonly CategoryBody[];
  /** Archived categories are behind this toggle, because they stay visible and keep their totals. */
  readonly showArchived: boolean;
  /** The shared write state, so a label's write is reported exactly as an amount's is. */
  readonly saveState: SaveState;
  readonly saveMessage: string;
  readonly localError: string | null;
  onToggleArchived(value: boolean): void;
  onCreate(name: string): void;
  onRename(category: CategoryTarget, name: string): void;
  onArchive(category: CategoryTarget, archived: boolean): void;
  onDelete(category: CategoryTarget): void;
}

export function CategoryPanel(props: CategoryPanelProps) {
  const [draft, setDraft] = useState('');
  const busy = props.saveState === 'saving';
  const views = categoryViews(props.categories);
  const shown = [
    ...categoriesFor(views, false),
    ...(props.showArchived ? categoriesFor(views, true) : []),
  ];

  return (
    <section className="panel" aria-labelledby="categories-heading" data-testid="category-panel">
      <h2 id="categories-heading">Categories</h2>

      <div className="actions">
        <label className="field">
          New category
          <input
            data-testid="category-name"
            autoComplete="off"
            maxLength={CATEGORY_NAME_LIMIT}
            value={draft}
            disabled={busy}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
        <button
          type="button"
          data-testid="category-create"
          disabled={busy}
          onClick={() => props.onCreate(draft)}
        >
          Create
        </button>
        <label className="field">
          <input
            type="checkbox"
            data-testid="category-archived-toggle"
            checked={props.showArchived}
            onChange={(event) => props.onToggleArchived(event.target.checked)}
          />
          Show archived (they keep their history and totals)
        </label>
      </div>

      {props.localError !== null ? (
        <p className="status-bad" role="alert" data-testid="category-error">
          {props.localError}
        </p>
      ) : null}

      {/*
        The write's own report, in the same vocabulary every other write uses. It is where a
        reference-safe refusal is read: the sentence names what still carries the category and
        offers archiving instead, so the refusal is visible rather than swallowed.
      */}
      <p
        role="status"
        data-testid="category-status"
        data-save-state={props.saveState}
        className={
          props.saveState === 'not-saved' || props.saveState === 'unknown' ? 'notice warn' : 'notice'
        }
        hidden={props.saveState === 'idle'}
      >
        {props.saveMessage === '' ? describeSaveState(props.saveState) : props.saveMessage}
      </p>

      <ul className="directory-list" data-testid="category-rows">
        {shown.map((category) => (
          <li key={category.id} data-testid="category-row" data-archived={category.archived}>
            <span data-testid="category-row-name">{category.name}</span>
            <span className="muted" data-testid="category-row-totals">
              {formatCategoryTotals(category)}
            </span>
            <span className="muted" data-testid="category-row-count">
              {category.entryCount} {category.entryCount === 1 ? 'entry' : 'entries'}
            </span>
            <CategoryRowActions
              category={category}
              busy={busy}
              onRename={props.onRename}
              onArchive={props.onArchive}
              onDelete={props.onDelete}
            />
          </li>
        ))}
      </ul>

      {shown.length === 0 ? (
        <p data-testid="category-empty">No categories yet — create one to label entries.</p>
      ) : null}
    </section>
  );
}

/**
 * One row's own controls.
 *
 * The rename draft is per row, so an edit in one row cannot appear in another's field, and the
 * row it belongs to is the one the API gets: the patch carries the row's `revision` as `If-Match`,
 * which is what makes a rename of a category someone else renamed a refusal rather than a
 * last-write-wins.
 */
function CategoryRowActions(props: {
  readonly category: CategoryView;
  readonly busy: boolean;
  onRename(category: CategoryTarget, name: string): void;
  onArchive(category: CategoryTarget, archived: boolean): void;
  onDelete(category: CategoryTarget): void;
}) {
  const { category, busy } = props;
  const [name, setName] = useState(category.name);
  return (
    <>
      <label className="field">
        Rename
        <input
          data-testid="category-rename-input"
          autoComplete="off"
          maxLength={CATEGORY_NAME_LIMIT}
          value={name}
          disabled={busy}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <button
        type="button"
        data-testid="category-rename-save"
        disabled={busy}
        onClick={() => props.onRename(category, name)}
      >
        Save name
      </button>
      <button
        type="button"
        data-testid="category-archive"
        disabled={busy}
        onClick={() => props.onArchive(category, !category.archived)}
      >
        {category.archived ? 'Unarchive' : 'Archive'}
      </button>
      <button
        type="button"
        data-testid="category-delete"
        disabled={busy}
        onClick={() => props.onDelete(category)}
      >
        Delete
      </button>
    </>
  );
}
